import React from 'react';

/**
 * App-level error boundary.
 *
 * Without this, any uncaught error thrown during render unmounts the whole React
 * tree and the user sees a blank white screen (e.g. the dashboard "click a model"
 * crash). This catches the error, logs it (with the component stack) so it is
 * debuggable, and shows a recoverable fallback instead of a blank page.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('App render error:', error, errorInfo && errorInfo.componentStack);
    this.setState({ componentStack: (errorInfo && errorInfo.componentStack) || '' });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.assign('/app');
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          textAlign: 'center',
          background: '#0b0b0d',
          color: '#fff',
          fontFamily:
            'Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        }}
      >
        <div style={{ maxWidth: 440 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>😕</div>
          <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700 }}>
            Something went wrong
          </h2>
          <p style={{ margin: '0 0 24px', color: '#9ca3af', fontSize: 14, lineHeight: 1.6 }}>
            This page hit an unexpected error. Your work and account are safe — try
            reloading, or head back to your dashboard.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={this.handleReload}
              style={{
                background: '#00bcd4',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Reload
            </button>
            <button
              onClick={this.handleHome}
              style={{
                background: 'transparent',
                color: '#fff',
                border: '1px solid #373737',
                borderRadius: 8,
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Back to dashboard
            </button>
          </div>
          {this.state.error && (
            <details
              style={{
                marginTop: 20,
                textAlign: 'left',
                background: '#161618',
                border: '1px solid #2a2a2a',
                borderRadius: 8,
                padding: 12,
              }}
            >
              <summary style={{ cursor: 'pointer', color: '#9ca3af', fontSize: 13 }}>
                Error details
              </summary>
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: '#f87171',
                  fontSize: 12,
                  marginTop: 8,
                  maxHeight: 240,
                  overflow: 'auto',
                }}
              >
                {String(
                  (this.state.error && (this.state.error.stack || this.state.error.message)) ||
                    this.state.error
                )}
                {this.state.componentStack
                  ? '\n\nComponent stack:' + this.state.componentStack
                  : ''}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
